"use client";

import { c as _c } from "react/compiler-runtime";
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { CheckIcon, ChevronDownIcon, ChevronsUpDownIcon, CircleAlertIcon, GripVerticalIcon, Trash2Icon } from "lucide-react";
import { nanoid } from "nanoid";
import { useState } from "react";
import useSWR from "swr";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn, invariant } from "@/lib/utils";
import { categoriesBrowseAction, collectionsBrowseAction, pagesBrowseAction } from "./actions";
function SortableMenuItem(t0) {
  const $ = _c(51);
  const {
    item,
    expandedItems,
    onAccordionChange,
    onDeleteItem,
    onItemUpdate,
    allowedTypes
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keepPreviousData: true
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    data: categoriesData
  } = useSWR("categoriesBrowseAction", _temp, t1);
  const categories = categoriesData?.data?.map(_temp2);
  const indentPerCategory = calculateCategoryDepth(categories);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      keepPreviousData: true
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    data: collectionsData
  } = useSWR("collectionsBrowseAction", _temp3, t2);
  const collections = collectionsData?.data?.map(_temp4);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      keepPreviousData: true
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const {
    data: pagesData
  } = useSWR("pagesBrowseAction", _temp5, t3);
  const pages = pagesData?.data?.map(_temp6);
  const data = {
    category: categories,
    "category-menu": categories,
    collection: collections,
    static: pages
  };
  let t4;
  if ($[3] !== item.id) {
    t4 = {
      id: item.id
    };
    $[3] = item.id;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable(t4);
  let t5;
  if ($[5] !== transform) {
    t5 = CSS.Translate.toString(transform);
    $[5] = transform;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const t6 = isDragging ? 10 : 1;
  const t7 = isDragging ? 0.8 : 1;
  let t8;
  if ($[7] !== t5 || $[8] !== t6 || $[9] !== t7 || $[10] !== transition) {
    t8 = {
      transform: t5,
      transition,
      zIndex: t6,
      opacity: t7
    };
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
    $[10] = transition;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const style = t8;
  const isExpanded = expandedItems.includes(item.id);
  const foundItem = item.type === "custom" || item.type === "category-dropdown" ? item : data[item.type]?.find(i => i.refId === item.refId);
  const t9 = isDragging && "shadow-lg";
  let t10;
  if ($[12] !== t9) {
    t10 = cn("border rounded bg-card overflow-hidden", t9);
    $[12] = t9;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <GripVerticalIcon className="h-4 w-4 text-muted-foreground" />;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== attributes || $[16] !== listeners) {
    t12 = <div {...attributes} {...listeners} className="cursor-grab">{t11}</div>;
    $[15] = attributes;
    $[16] = listeners;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  const t13 = !foundItem ? "\xA0" : "label" in item ? item.label : ("refId" in foundItem ? foundItem.name : foundItem.label) || "\xA0";
  let t14;
  if ($[18] !== t13) {
    t14 = <div><p className="text-sm font-medium">{t13}</p></div>;
    $[18] = t13;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] !== t12 || $[21] !== t14) {
    t15 = <div className="flex items-center gap-3">{t12}{t14}</div>;
    $[20] = t12;
    $[21] = t14;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] !== item.id || $[24] !== onAccordionChange) {
    t16 = e => {
      e.stopPropagation();
      onAccordionChange(item.id);
    };
    $[23] = item.id;
    $[24] = onAccordionChange;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  const t17 = cn("h-4 w-4 transition-transform duration-200", isExpanded && "rotate-180");
  let t18;
  if ($[26] !== t17) {
    t18 = <ChevronDownIcon className={t17} />;
    $[26] = t17;
    $[27] = t18;
  } else {
    t18 = $[27];
  }
  let t19;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <span className="sr-only">Toggle</span>;
    $[28] = t19;
  } else {
    t19 = $[28];
  }
  let t20;
  if ($[29] !== t16 || $[30] !== t18) {
    t20 = <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={t16}>{t18}{t19}</Button>;
    $[29] = t16;
    $[30] = t18;
    $[31] = t20;
  } else {
    t20 = $[31];
  }
  let t21;
  if ($[32] !== item.id || $[33] !== onDeleteItem) {
    t21 = e_0 => {
      e_0.stopPropagation();
      onDeleteItem(item.id);
    };
    $[32] = item.id;
    $[33] = onDeleteItem;
    $[34] = t21;
  } else {
    t21 = $[34];
  }
  let t22;
  let t23;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <Trash2Icon className="h-4 w-4" />;
    t23 = <span className="sr-only">Delete</span>;
    $[35] = t22;
    $[36] = t23;
  } else {
    t22 = $[35];
    t23 = $[36];
  }
  let t24;
  if ($[37] !== t21) {
    t24 = <Button type="button" variant="ghost" size="icon" onClick={t21}>{t22}{t23}</Button>;
    $[37] = t21;
    $[38] = t24;
  } else {
    t24 = $[38];
  }
  let t25;
  if ($[39] !== t20 || $[40] !== t24) {
    t25 = <div className="flex items-center gap-2">{t20}{t24}</div>;
    $[39] = t20;
    $[40] = t24;
    $[41] = t25;
  } else {
    t25 = $[41];
  }
  let t26;
  if ($[42] !== t15 || $[43] !== t25) {
    t26 = <div className="flex items-center justify-between px-3 py-2">{t15}{t25}</div>;
    $[42] = t15;
    $[43] = t25;
    $[44] = t26;
  } else {
    t26 = $[44];
  }
  const t27 = isExpanded && <div className="pb-3 px-3 space-y-4 pt-2 border-t"><Tabs value={item.type === "custom" ? "custom" : "pages"} onValueChange={value_ => {
      const value = value_;
      if (value === "custom") {
        onItemUpdate(item.id, {
          id: item.id,
          type: "custom",
          label: "",
          url: ""
        });
      } else {
        onItemUpdate(item.id, {
          id: item.id,
          type: "category",
          refId: undefined
        });
      }
    }} className="pt-3"><TabsList className="grid w-full grid-cols-2"><TabsTrigger value="pages">Pages</TabsTrigger><TabsTrigger value="custom">Custom URL</TabsTrigger></TabsList></Tabs>{item.type !== "custom" ? <div className="space-y-4"><div className="space-y-2"><Label>Page Type</Label><Select value={item.type} onValueChange={value_0 => {
          if (value_0 === "custom") {
            onItemUpdate(item.id, {
              id: item.id,
              type: value_0,
              label: "",
              url: ""
            });
          } else {
            if (value_0 === "category-dropdown") {
              onItemUpdate(item.id, {
                id: item.id,
                type: value_0,
                label: ""
              });
            } else {
              if (value_0 === "category-menu") {
                onItemUpdate(item.id, {
                  id: item.id,
                  type: value_0,
                  refId: undefined,
                  expandable: true
                });
              } else {
                onItemUpdate(item.id, {
                  id: item.id,
                  type: value_0,
                  refId: undefined
                });
              }
            }
          }
        }}><SelectTrigger className="w-full"><SelectValue placeholder="Select page type" /></SelectTrigger><SelectContent>{(!allowedTypes || allowedTypes.includes("category")) && <SelectItem value="category">Single Category</SelectItem>}{(!allowedTypes || allowedTypes.includes("collection")) && <SelectItem value="collection">Single Collection</SelectItem>}{(!allowedTypes || allowedTypes.includes("category-menu")) && <SelectItem value="category-menu">Category Menu</SelectItem>}{(!allowedTypes || allowedTypes.includes("static")) && <SelectItem value="static">Static Page</SelectItem>}{(!allowedTypes || allowedTypes.includes("category-dropdown")) && <SelectItem value="category-dropdown" className="text-xs text-neutral-500"><Tooltip><TooltipTrigger tabIndex={0} asChild={true} className="p-0 m-0 h-auto w-auto"><Button type="button" variant="ghost" size="icon" className="p-0"><CircleAlertIcon className="size-4 stroke-gray-700" /></Button></TooltipTrigger><TooltipContent>Deprecated. Use Category Menu instead.</TooltipContent></Tooltip>Categories Dropdown</SelectItem>}</SelectContent></Select></div>{item.type === "category-dropdown" ? <div className="space-y-2"><Label htmlFor={`label-${item.id}`}>Label</Label><Input id={`label-${item.id}`} value={item.label} onChange={e_1 => onItemUpdate(item.id, {
          ...item,
          label: e_1.target.value
        })} placeholder="Menu item label" /></div> : <><div className="space-y-2"><Label>Select {item.type === "category-menu" ? "category" : item.type}</Label><Popover><PopoverTrigger asChild={true}><Button type="button" variant="outline" role="combobox" className="w-full justify-between">{!foundItem ? `Select a ${item.type === "category-menu" ? "category" : item.type}` : "refId" in foundItem ? foundItem.name : foundItem.label}<ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" /></Button></PopoverTrigger><PopoverContent className="p-0 w-[317px]"><Command><CommandInput placeholder={`Search ${item.type === "category-menu" ? "category" : item.type}...`} /><CommandList><CommandEmpty>No {item.type} found.</CommandEmpty><CommandGroup>{(item.type === "category" || item.type === "category-menu" ? categories : item.type === "collection" ? collections : pages)?.map(selectableItem => <CommandItem key={selectableItem.refId} value={selectableItem.slug + " " + selectableItem.name + " " + item.refId} onSelect={() => {
                      onItemUpdate(item.id, {
                        id: item.id,
                        type: item.type,
                        refId: selectableItem.refId
                      });
                    }}><CheckIcon className={cn("mr-2 h-4 w-4", item.refId === selectableItem.refId ? "opacity-100" : "opacity-0")} /><div style={{
                        paddingLeft: indentPerCategory.get(selectableItem.refId) + "rem"
                      }}>{selectableItem.name}</div></CommandItem>)}</CommandGroup></CommandList></Command></PopoverContent></Popover></div>{item.type === "category-menu" ? <div className="space-y-2"><Label>Should category menu be expandable</Label><Select onValueChange={expandable => onItemUpdate(item.id, {
            ...item,
            expandable: expandable === "true"
          })} value={(item.expandable ?? false).toString()}><SelectTrigger className="w-full"><SelectValue placeholder="Is expandable?" /></SelectTrigger><SelectContent className="w-full"><SelectItem value="true">Yes</SelectItem><SelectItem value="false">No</SelectItem></SelectContent></Select></div> : null}{item.type === "static" ? <div className="space-y-2"><Label htmlFor={`label-${item.id}`}>Label</Label><Input id={`label-${item.id}`} value={item.label} onChange={e_2 => onItemUpdate(item.id, {
            ...item,
            label: e_2.target.value
          })} placeholder="Menu item label" /></div> : null}</>}</div> : <div className="space-y-4"><div className="space-y-2"><Label htmlFor={`label-${item.id}`}>Label</Label><Input id={`label-${item.id}`} value={item.label} onChange={e_3 => onItemUpdate(item.id, {
          ...item,
          label: e_3.target.value
        })} placeholder="Menu item label" /></div><div className="space-y-2"><Label htmlFor={`url-${item.id}`}>URL</Label><Input id={`url-${item.id}`} value={item.url} onChange={e_4 => onItemUpdate(item.id, {
          ...item,
          url: e_4.target.value
        })} placeholder="https://example.com/page" /></div></div>}</div>;
  let t28;
  if ($[45] !== setNodeRef || $[46] !== style || $[47] !== t10 || $[48] !== t26 || $[49] !== t27) {
    t28 = <div ref={setNodeRef} style={style} className={t10}>{t26}{t27}</div>;
    $[45] = setNodeRef;
    $[46] = style;
    $[47] = t10;
    $[48] = t26;
    $[49] = t27;
    $[50] = t28;
  } else {
    t28 = $[50];
  }
  return t28;
}
function _temp6(c_1) {
  return {
    refId: c_1.id,
    name: c_1.path,
    slug: c_1.path
  };
}
function _temp5() {
  return pagesBrowseAction();
}
function _temp4(c_0) {
  return {
    refId: c_0.id,
    name: c_0.name,
    slug: c_0.slug
  };
}
function _temp3() {
  return collectionsBrowseAction();
}
function _temp2(c) {
  return {
    refId: c.id,
    name: c.name,
    slug: c.slug,
    parentId: c.parentId
  };
}
function _temp() {
  return categoriesBrowseAction();
}
export function MenuEditor(t0) {
  const $ = _c(50);
  const {
    items,
    onChange,
    allowedTypes
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [expandedItems, setExpandedItems] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      activationConstraint: {
        distance: 8
      }
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      coordinateGetter: sortableKeyboardCoordinates
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const sensors = useSensors(useSensor(PointerSensor, t2), useSensor(KeyboardSensor, t3));
  let t4;
  if ($[3] !== allowedTypes?.[0] || $[4] !== expandedItems || $[5] !== items || $[6] !== onChange) {
    t4 = () => {
      const newItem = allowedTypes?.[0] === "custom" || allowedTypes?.[0] === "category-dropdown" ? {
        id: nanoid(),
        type: allowedTypes?.[0],
        label: "",
        url: ""
      } : {
        id: nanoid(),
        refId: undefined,
        type: allowedTypes?.[0] ?? "category"
      };
      onChange([...items, newItem]);
      setExpandedItems([...expandedItems, newItem.id]);
    };
    $[3] = allowedTypes?.[0];
    $[4] = expandedItems;
    $[5] = items;
    $[6] = onChange;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const addNewItem = t4;
  let t5;
  if ($[8] !== items || $[9] !== onChange) {
    t5 = (id, newItem_0) => {
      const updatedItems = items.map(item => item.id === id ? newItem_0 : item);
      onChange(updatedItems);
    };
    $[8] = items;
    $[9] = onChange;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const handleItemUpdate = t5;
  let t6;
  if ($[11] !== expandedItems || $[12] !== items || $[13] !== onChange) {
    t6 = id_0 => {
      onChange(items.filter(item_0 => item_0.id !== id_0));
      setExpandedItems(expandedItems.filter(itemId => itemId !== id_0));
    };
    $[11] = expandedItems;
    $[12] = items;
    $[13] = onChange;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const handleDeleteItem = t6;
  let t7;
  if ($[15] !== expandedItems || $[16] !== items) {
    t7 = id_1 => {
      if (expandedItems.includes(id_1)) {
        setExpandedItems(expandedItems.filter(item_1 => item_1 !== id_1));
      } else {
        setExpandedItems([...expandedItems, id_1]);
        const item_3 = items.find(item_2 => item_2.id === id_1);
        invariant(item_3, "Item not found");
      }
    };
    $[15] = expandedItems;
    $[16] = items;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const handleAccordionChange = t7;
  let t8;
  if ($[18] !== items || $[19] !== onChange) {
    t8 = event => {
      const {
        active,
        over
      } = event;
      if (!over) {
        return;
      }
      if (active.id !== over.id) {
        const oldIndex = items.findIndex(item_4 => item_4.id === active.id);
        const newIndex = items.findIndex(item_5 => item_5.id === over.id);
        const result = arrayMove(items, oldIndex, newIndex);
        onChange(result);
      }
    };
    $[18] = items;
    $[19] = onChange;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const handleDragEnd = t8;
  let t9;
  if ($[21] !== items) {
    t9 = items.map(_temp7);
    $[21] = items;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== allowedTypes || $[24] !== expandedItems || $[25] !== handleAccordionChange || $[26] !== handleDeleteItem || $[27] !== handleItemUpdate || $[28] !== items) {
    let t11;
    if ($[30] !== allowedTypes || $[31] !== expandedItems || $[32] !== handleAccordionChange || $[33] !== handleDeleteItem || $[34] !== handleItemUpdate) {
      t11 = item_7 => <SortableMenuItem key={item_7.id} item={item_7} expandedItems={expandedItems} onAccordionChange={handleAccordionChange} onDeleteItem={handleDeleteItem} onItemUpdate={handleItemUpdate} allowedTypes={allowedTypes} />;
      $[30] = allowedTypes;
      $[31] = expandedItems;
      $[32] = handleAccordionChange;
      $[33] = handleDeleteItem;
      $[34] = handleItemUpdate;
      $[35] = t11;
    } else {
      t11 = $[35];
    }
    t10 = items.map(t11);
    $[23] = allowedTypes;
    $[24] = expandedItems;
    $[25] = handleAccordionChange;
    $[26] = handleDeleteItem;
    $[27] = handleItemUpdate;
    $[28] = items;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[36] !== t10) {
    t11 = <div className="space-y-1">{t10}</div>;
    $[36] = t10;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  let t12;
  if ($[38] !== t11 || $[39] !== t9) {
    t12 = <SortableContext items={t9} strategy={verticalListSortingStrategy}>{t11}</SortableContext>;
    $[38] = t11;
    $[39] = t9;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  let t13;
  if ($[41] !== handleDragEnd || $[42] !== sensors || $[43] !== t12) {
    t13 = <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>{t12}</DndContext>;
    $[41] = handleDragEnd;
    $[42] = sensors;
    $[43] = t12;
    $[44] = t13;
  } else {
    t13 = $[44];
  }
  let t14;
  if ($[45] !== addNewItem) {
    t14 = <Button type="button" className="w-full rounded-full" onClick={addNewItem}>Add Menu Item</Button>;
    $[45] = addNewItem;
    $[46] = t14;
  } else {
    t14 = $[46];
  }
  let t15;
  if ($[47] !== t13 || $[48] !== t14) {
    t15 = <div className="space-y-2">{t13}{t14}</div>;
    $[47] = t13;
    $[48] = t14;
    $[49] = t15;
  } else {
    t15 = $[49];
  }
  return t15;
}
function _temp7(item_6) {
  return item_6.id;
}
function calculateCategoryDepth(categories) {
  const indentPerCategory = new Map();

  // Calculate nesting depth for each category
  const calculateDepth = (categoryId, visited = new Set()) => {
    // If we've already calculated depth for this category, return it
    if (indentPerCategory.has(categoryId)) {
      return indentPerCategory.get(categoryId) ?? 0;
    }

    // Detect circular references
    if (visited.has(categoryId)) {
      return 0;
    }
    visited.add(categoryId);
    const category = categories?.find(c => c.refId === categoryId);
    if (!category || !category.parentId) {
      indentPerCategory.set(categoryId, 0);
      return 0;
    }
    const parentDepth = calculateDepth(category.parentId, visited);
    const depth = parentDepth + 1;
    indentPerCategory.set(categoryId, depth);
    return depth;
  };

  // Calculate depth for all categories
  categories?.forEach(category => {
    if (!indentPerCategory.has(category.refId)) {
      calculateDepth(category.refId);
    }
  });
  return indentPerCategory;
}